import { useMemo } from 'react';

import { OrderOperationType, OrderStatusType } from 'api/types/entity';
import { FormTabs } from 'components';
import { isFinanceProject } from 'config';
import { lodash } from 'helpers';
import { useTranslation } from 'libs/i18n';
import {
  AdditionalParametersSection,
  ConfirmationDocumentSection,
  ExecutionPeriodSection,
  ExecutionTermsSection,
  FinancialInstrumentSection,
  SenderOrRecipientDetailsSection,
  TransactionVolumeSection,
  TransferVolumeSection,
} from 'modules/order/common/components';

import { useToggleEditable } from '../../hooks/useToggleEditable';
import { useOrderContext } from '../../providers/OrderProvider';
import { OrderFormTab } from '../../types';
import {
  AdditionalNotesSection,
  ExecutionHistorySection,
  MainInformationSection,
} from '../sections';

export interface OrderFormContentProps {
  tab: OrderFormTab;
  onTabChange: (tab: OrderFormTab) => void;
}

export const OrderFormContent = ({
  tab,
  onTabChange,
}: OrderFormContentProps) => {
  const { t } = useTranslation();

  const { editable, disableEditable } = useToggleEditable();

  const { orderType, order } = useOrderContext();

  const isPurchaseOrSale =
    orderType === OrderOperationType.SecuritiesPurchase ||
    orderType === OrderOperationType.SecuritiesSale;

  const isWithdrawalOrDeposit =
    orderType === OrderOperationType.SecuritiesWithdrawal ||
    orderType === OrderOperationType.SecuritiesDeposit;

  const isFinance = isFinanceProject();

  const tabs = useMemo(
    () =>
      lodash.compact([
        {
          label: t('order.order.orderDetailsTabLabel'),
          id: OrderFormTab.orderDetails,
          content: (
            <>
              <MainInformationSection />
              <ExecutionPeriodSection
                closedDate={{ show: true, value: order?.closedDate }}
                signedAt={{ show: true, value: order?.signedAt }}
              />
              <ExecutionTermsSection
                isShowOperationKind={isWithdrawalOrDeposit}
                isShowOperationPurpose={isPurchaseOrSale && isFinance}
                isShowOrderType={isPurchaseOrSale}
                isShowYield={isPurchaseOrSale}
              />
              <FinancialInstrumentSection />
              {isPurchaseOrSale && <TransactionVolumeSection />}
              {isWithdrawalOrDeposit && <TransferVolumeSection />}
              {isWithdrawalOrDeposit && (
                <SenderOrRecipientDetailsSection type="sender" />
              )}
              {isWithdrawalOrDeposit && (
                <SenderOrRecipientDetailsSection type="recipient" />
              )}
              {isWithdrawalOrDeposit && <AdditionalParametersSection />}
              {isWithdrawalOrDeposit && <ConfirmationDocumentSection />}
              <AdditionalNotesSection />
            </>
          ),
          formSectionKeys: [
            'endDate',
            'startDate',
            'executionTerms',
            'financialInstrument',
            'transactionVolume',
            'transferVolume',
            'senderDetails',
            'recipientDetails',
            'additionalParameters',
            'confirmationDocument',
            'additionalNotes',
          ],
        },

        {
          label: t('order.order.executionHistoryTabLabel'),
          id: OrderFormTab.executionHistory,
          content: <ExecutionHistorySection />,
          formSectionKeys: [
            'fmNumber',
            'manager',
            'marketType',
            'transactionVenue',
            'settlementCondition',
            'adminDocuments',
            'hasBrokerRecommendation',
            'comment',
          ],
        },
      ]),
    [
      isFinance,
      isPurchaseOrSale,
      isWithdrawalOrDeposit,
      order?.closedDate,
      order?.signedAt,
      t,
    ]
  );

  const handleTabChange = (
    event: React.SyntheticEvent,
    tabId: OrderFormTab
  ) => {
    if (
      order?.status !== OrderStatusType.AwaitingSigning &&
      tab === OrderFormTab.executionHistory &&
      tabId !== OrderFormTab.executionHistory &&
      editable
    ) {
      const promptResult = disableEditable();
      // if user confirmed cancellation, or there were no changes
      // proceed with tab change
      if (promptResult === true || promptResult === null) {
        onTabChange(tabId);
      } else {
        event.preventDefault(); // Removed as event is no longer passed
      }
    } else {
      onTabChange(tabId);
    }
  };

  return (
    <FormTabs
      tabs={tabs}
      onInitialTab={onTabChange}
      onTabChange={handleTabChange}
    />
  );
};
